import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api, { getApiErrors } from '@app/services/api';
import type { ApiError } from '@app/services/api.types';
import type { AIFlowSchema } from '@app/services/aiFlow.types';
import { apiEndpoints, defaultErrorMessage } from '@app/utils/constants';
import useApp from '../useApp';

type FlowParams = { name: string; schema: AIFlowSchema };

export const createFlow = async (params: FlowParams) => {
    const url = apiEndpoints.aiFlows.add;
    const response = await api.post(url, params);
    return response?.data;
};

export const updateFlow = async (params: { id: string; schema: FlowParams }) => {
    const { id, schema } = params;
    const url = apiEndpoints.aiFlows.update(id);
    const response = await api.put(url, schema);
    return response?.data?.data;
};

export const removeFlow = async (params: { id: string }) => {
    const url = apiEndpoints.aiFlows.delete(params.id);
    const response = await api.delete(url);
    return response?.data;
};

const useFlowActions = () => {
    const queryClient = useQueryClient();
    const { message } = useApp();

    const addFlow = useMutation({
        mutationFn: createFlow,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'AI Agent Flow added successfully',
            });
            queryClient.invalidateQueries({ queryKey: ['ai-flow', 'list'] });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.error, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });
    const editFlow = useMutation({
        mutationFn: updateFlow,
        onSuccess: (data) => {
            message.success({
                type: 'success',
                content: 'AI Agent Flow updated successfully',
            });
            queryClient.setQueryData(['ai-flow', 'single', { id: data.id }], data);
            queryClient.invalidateQueries({ queryKey: ['ai-flow', 'list'] });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.error, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });
    const deleteFlow = useMutation({
        mutationFn: removeFlow,
        onSuccess: () => {
            message.success({
                type: 'success',
                content: 'AI Agent Flow deleted successfully',
            });
            queryClient.invalidateQueries({ queryKey: ['ai-flow', 'list'] });
        },
        onError: (error: AxiosError<ApiError>) => {
            const response = error.response?.data;
            const errorMessage = getApiErrors(response?.error, true);
            message.error({
                type: 'error',
                content: errorMessage || defaultErrorMessage,
            });
        },
    });

    return {
        addFlow,
        editFlow,
        deleteFlow,
    };
};

export default useFlowActions;
